import React, { useMemo, useState } from 'react';
import { Check, Search, Share2 } from 'lucide-react';
import { useCalendarSharingStore } from '../../lib/stores/calendarSharingStore';
import { colorForPerson } from '../../lib/calendar/presentActivities';
import { HelpTip } from '../ui/HelpTip';
import { ShareMyCalendarDialog } from './ShareMyCalendarDialog';
import { PersonName } from '../shared/PersonName';

/**
 * Barra del calendario, sezione "Calendari": il mio e i colleghi che vedo,
 * raggruppati per reparto, con spunta e colore (come i calendari di Google).
 */
function Tick({ on, color, onClick, label }: { on: boolean; color: string; onClick: (e: React.MouseEvent) => void; label: string }) {
  return (
    <button type="button" aria-label={label} aria-pressed={on} onClick={onClick} className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0">
      {on
        ? <span style={{ backgroundColor: color }} className="w-full h-full rounded flex items-center justify-center"><Check className="w-2.5 h-2.5 text-white" strokeWidth={3} /></span>
        : <span className="w-full h-full rounded border-2 border-gray-300 dark:border-gray-600" />}
    </button>
  );
}

export function PeopleCalendarsSection() {
  const { ready, ctx, people, departments, visible, shown, prefs, togglePerson, showOnly, showAll } = useCalendarSharingStore();
  const [query, setQuery] = useState('');
  const [shareOpen, setShareOpen] = useState(false);

  const meId = ctx?.meId || '';
  const ordered = useMemo(() => people.map(p => p.id), [people]);
  const others = useMemo(() => people.filter(p => p.id !== meId && visible[p.id]), [people, meId, visible]);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const match = (name: string) => !q || name.toLowerCase().includes(q);
    const out: { id: string; name: string; members: typeof others }[] = [];
    for (const d of departments) {
      const members = others.filter(p => p.departmentIds.includes(d.id) && match(p.name));
      if (members.length) out.push({ id: d.id, name: d.name, members });
    }
    const rest = others.filter(p => !p.departmentIds.some(id => departments.some(d => d.id === id)) && match(p.name));
    if (rest.length) out.push({ id: 'none', name: departments.length ? 'Senza reparto' : 'Colleghi', members: rest });
    return out;
  }, [departments, others, query]);

  if (!ready || !ctx) return null;
  const meColor = colorForPerson(meId, meId, ordered, prefs.person_colors);

  return (
    <div className="mb-4 flex-shrink-0">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold uppercase text-black/60 dark:text-white/60 flex items-center gap-1.5">
          Calendari
          <HelpTip
            size={14}
            title="Calendari delle persone"
            text="Ogni persona ha il suo calendario: le task commerciali assegnate a lei. Qui accendi e spegni quelli che puoi vedere. Chi vedi dipende dalle condivisioni e dai permessi."
            example="Le operatrici vedono acceso il calendario di ogni consulente: così fissano gli appuntamenti negli orari liberi."
          />
        </span>
        <button type="button" onClick={() => setShareOpen(true)} className="text-xs text-black/60 dark:text-white/60 hover:text-black/90 hover:bg-black/5 dark:hover:bg-white/5 rounded px-1.5 py-0.5 flex items-center gap-1">
          <Share2 className="w-3 h-3" strokeWidth={1.5} /> Condividi
        </button>
      </div>

      <div className="flex items-center gap-3 py-1.5 px-2 rounded hover:bg-black/5 dark:hover:bg-white/5 cursor-pointer" onClick={() => togglePerson(meId)}>
        <Tick on={!!shown[meId]} color={meColor} label="Mostra il mio calendario" onClick={(e) => { e.stopPropagation(); togglePerson(meId); }} />
        <span className="text-sm flex-1 text-black/80 dark:text-white/80">Il mio calendario</span>
      </div>
      {others.length > 10 && (
        <label className="mt-2 mb-1 flex items-center gap-2 rounded px-2 py-1.5 bg-black/5 dark:bg-white/5">
          <Search className="w-3.5 h-3.5 text-black/40 dark:text-white/40" strokeWidth={1.5} />
          <span className="sr-only">Cerca persona</span>
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Cerca persona" className="flex-1 bg-transparent text-sm outline-none text-black/80 dark:text-white/80" />
        </label>
      )}

      {groups.map(g => {
        const ids = g.members.map(m => m.id);
        const on = ids.filter(id => shown[id]).length;
        return (
          <div key={g.id} className="mt-2">
            <div className="flex items-center justify-between px-2 py-1 text-xs font-medium text-black/60 dark:text-white/60">
              <span>{g.name} · {on} di {ids.length}</span>
              <button type="button" className="font-normal text-black/60 dark:text-white/60 hover:text-black/90 dark:hover:text-white/90" onClick={() => showAll(ids, on < ids.length)}>
                {on < ids.length ? 'Mostra tutti' : 'Nascondi tutti'}
              </button>
            </div>
            {g.members.map(p => {
              const color = colorForPerson(p.id, meId, ordered, prefs.person_colors);
              const busy = visible[p.id] === 'busy';
              return (
                <div key={p.id} className="group flex items-center gap-3 py-1.5 px-2 rounded hover:bg-black/5 dark:hover:bg-white/5 cursor-pointer" onClick={() => togglePerson(p.id)}>
                  <Tick on={!!shown[p.id]} color={busy ? '#9CA3AF' : color} label={`Mostra il calendario di ${p.name}`} onClick={(e) => { e.stopPropagation(); togglePerson(p.id); }} />
                  <PersonName name={p.name} className={`text-sm flex-1 ${shown[p.id] ? 'text-black/80 dark:text-white/80' : 'text-black/50 dark:text-white/50'}`} />
                  {busy && <span className="text-[10px] text-black/50 dark:text-white/50 group-hover:hidden">solo occupato</span>}
                  <button type="button" className="hidden group-hover:inline text-[11px] text-black/60 dark:text-white/60 hover:text-black/90" onClick={(e) => { e.stopPropagation(); showOnly([p.id]); }}>Solo questo</button>
                </div>
              );
            })}
          </div>
        );
      })}

      {shareOpen && <ShareMyCalendarDialog onClose={() => setShareOpen(false)} />}
    </div>
  );
}
